<template>
  <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
    <!-- Header -->
    <div class="mb-8">
      <h1 class="text-2xl font-bold text-gray-900">Dashboard</h1>
      <p class="mt-1 text-gray-600">
        Welcome back! Here's your spark activity overview.
      </p>
    </div>

    <!-- Stats Grid -->
    <div data-tour="stats-grid" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
      <!-- Current Balance -->
      <div class="card group">
        <div class="flex items-center">
          <div class="stat-icon yellow mr-4">
            <PhLightning class="w-6 h-6" weight="fill" />
          </div>
          <div>
            <p class="text-sm text-gray-600">Current Balance</p>
            <p class="text-2xl font-bold font-mono text-gray-900">
              {{ sparksStore.formattedBalance }}
            </p>
          </div>
        </div>
      </div>

      <!-- Lifetime Earned -->
      <div class="card group">
        <div class="flex items-center">
          <div class="stat-icon green mr-4">
            <PhTrendUp class="w-6 h-6" weight="fill" />
          </div>
          <div>
            <p class="text-sm text-gray-600">Lifetime Earned</p>
            <p class="text-2xl font-bold font-mono text-gray-900">
              {{ sparksStore.lifetimeEarned.toLocaleString() }}
            </p>
          </div>
        </div>
      </div>

      <!-- Lifetime Spent -->
      <div class="card group">
        <div class="flex items-center">
          <div class="stat-icon blue mr-4">
            <PhCoins class="w-6 h-6" weight="fill" />
          </div>
          <div>
            <p class="text-sm text-gray-600">Lifetime Spent</p>
            <p class="text-2xl font-bold font-mono text-gray-900">
              {{ sparksStore.lifetimeSpent.toLocaleString() }}
            </p>
          </div>
        </div>
      </div>

      <!-- Active Subscription -->
      <div class="card group">
        <div class="flex items-center">
          <div class="stat-icon" :class="subscriptionIconBg">
            <component :is="subscriptionIcon" class="w-6 h-6" weight="fill" />
          </div>
          <div class="ml-4">
            <p class="text-sm text-gray-600">Subscription</p>
            <p class="text-lg font-semibold text-gray-900">
              {{ subscriptionStatus }}
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- Main Content Grid -->
    <div class="grid grid-cols-1 lg:grid-cols-3 gap-8">
      <!-- Left Column: Charts -->
      <div class="lg:col-span-2 space-y-6">
        <!-- Usage Chart -->
        <div class="card">
          <div class="flex items-center justify-between mb-6">
            <h3 class="text-lg font-semibold text-gray-900">Usage Overview</h3>
            <select
              v-model="chartPeriod"
              class="text-sm rounded-xl px-3 py-2 transition-all duration-300 bg-white border border-gray-300 text-gray-900 focus:border-neon-blue"
            >
              <option value="7">Last 7 days</option>
              <option value="30">Last 30 days</option>
              <option value="90">Last 90 days</option>
            </select>
          </div>

          <div class="h-64">
            <UsageChart
              :data="chartData"
              :period="Number(chartPeriod)"
              :loading="sparksStore.isLoading"
              :is-dark="false"
            />
          </div>
        </div>

        <!-- Product Usage -->
        <div class="card">
          <h3 class="text-lg font-semibold mb-6 text-gray-900">
            Product Usage
          </h3>
          <div class="space-y-4">
            <div
              v-for="product in productUsage"
              :key="product.name"
              class="flex items-center justify-between p-3 rounded-xl transition-all duration-200 hover:bg-gray-50"
            >
              <div class="flex items-center space-x-3">
                <div class="stat-icon green w-10 h-10">
                  <component :is="product.icon" class="w-5 h-5" weight="fill" />
                </div>
                <div>
                  <p class="font-medium text-gray-900">{{ product.name }}</p>
                  <p class="text-sm text-gray-500">{{ product.description }}</p>
                </div>
              </div>
              <div class="text-right">
                <p class="font-mono font-medium text-gray-900">
                  {{ product.sparksUsed.toLocaleString() }} sparks
                </p>
                <p class="text-sm text-gray-500">{{ product.percentage }}%</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Right Column: Sidebar -->
      <div class="space-y-6">
        <!-- Quick Actions -->
        <div data-tour="quick-actions" class="card">
          <h3 class="text-lg font-semibold mb-4 text-gray-900">
            Quick Actions
          </h3>
          <div class="space-y-3">
            <router-link
              to="/buy"
              data-tour="buy-sparks"
              class="w-full btn-primary flex items-center justify-center"
            >
              <PhCreditCard class="w-5 h-5 mr-2" weight="fill" />
              Buy Sparks
            </router-link>

            <router-link
              to="/subscription"
              class="w-full btn-secondary flex items-center justify-center"
            >
              <PhArrowsClockwise class="w-5 h-5 mr-2" />
              Manage Subscription
            </router-link>

            <router-link
              to="/usage"
              class="w-full btn-secondary flex items-center justify-center"
            >
              <PhChartBar class="w-5 h-5 mr-2" weight="fill" />
              View Detailed Usage
            </router-link>
          </div>
        </div>

        <!-- Recent Activity -->
        <div class="card">
          <div class="flex items-center justify-between mb-4">
            <h3 class="text-lg font-semibold text-gray-900">Recent Activity</h3>
            <button
              v-if="sparksStore.isInitialized"
              @click="handleRefreshTransactions"
              class="text-sm font-medium transition-colors text-primary hover:text-primary/80"
            >
              Refresh
            </button>
          </div>

          <div class="space-y-3">
            <div
              v-for="transaction in sparksStore.recentTransactions"
              :key="transaction.id"
              class="flex items-center justify-between py-3 border-b last:border-b-0 border-gray-100"
            >
              <div class="flex-1">
                <p class="text-sm font-medium text-gray-900">
                  {{ transaction.action }}
                </p>
                <p class="text-xs text-gray-500">
                  {{ formatProductName(transaction.product) }}
                </p>
                <p class="text-xs text-gray-400">
                  {{ formatDate(transaction.created_at) }}
                </p>
              </div>

              <div class="text-right">
                <p
                  class="text-sm font-mono font-medium"
                  :class="transaction.sparks_spent ? 'text-neon-pink' : 'text-neon-green'"
                >
                  {{ transaction.sparks_spent ? "-" : "+"
                  }}{{
                    (
                      transaction.sparks_spent ||
                      transaction.sparks_added ||
                      0
                    ).toLocaleString()
                  }}
                </p>
                <p class="text-xs font-mono text-gray-500">
                  Balance: {{ transaction.balance_after.toLocaleString() }}
                </p>
              </div>
            </div>

            <div
              v-if="sparksStore.recentTransactions.length === 0"
              class="text-center py-4"
            >
              <p class="text-gray-500">No recent activity</p>
            </div>
          </div>
        </div>

        <!-- Subscription Status -->
        <div v-if="sparksStore.hasActiveSubscription" class="card">
          <h3 class="text-lg font-semibold mb-4 text-gray-900">
            Subscription Status
          </h3>
          <div class="space-y-3">
            <div class="flex justify-between">
              <span class="text-sm text-gray-600">Plan:</span>
              <span class="text-sm font-medium text-gray-900">{{
                activeSubscription?.plan?.name
              }}</span>
            </div>

            <div class="flex justify-between">
              <span class="text-sm text-gray-600">Monthly Sparks:</span>
              <span class="text-sm font-medium font-mono text-gray-900">
                {{
                  activeSubscription?.plan?.sparks_per_month.toLocaleString()
                }}
              </span>
            </div>

            <div class="flex justify-between">
              <span class="text-sm text-gray-600">Next Billing:</span>
              <span class="text-sm font-medium text-gray-900">
                {{ formatDate(activeSubscription?.current_period_end) }}
              </span>
            </div>

            <div
              v-if="sparksStore.subscriptionWillCancel"
              class="rounded-xl p-3 border bg-orange-50 border-orange-200"
            >
              <p class="text-sm flex items-center text-orange-800">
                <PhWarning class="w-4 h-4 mr-1.5 flex-shrink-0" weight="fill" />
                Your subscription will be canceled at the end of the current
                period.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, nextTick, watch, type Component } from "vue";
import {
  PhLightning,
  PhTrendUp,
  PhCoins,
  PhXCircle,
  PhWarning,
  PhCheckCircle,
  PhCreditCard,
  PhArrowsClockwise,
  PhChartBar,
  PhMaskHappy,
  PhFilmSlate,
  PhGear,
  PhSquaresFour,
} from "@phosphor-icons/vue";
import { useSparksStore } from "../stores/sparks";
import UsageChart from "../components/UsageChart.vue";
import { useWelcomeTour } from "../composables/useWelcomeTour";

const sparksStore = useSparksStore();
const welcomeTour = useWelcomeTour();

// State
const chartPeriod = ref(30);

// Computed
const activeSubscription = computed(() => sparksStore.activeSubscription);

const subscriptionStatus = computed(() => {
  if (!sparksStore.hasActiveSubscription) return "No Subscription";
  if (sparksStore.subscriptionWillCancel) return "Canceling";
  return sparksStore.activeSubscription?.plan?.name || "Active";
});

const subscriptionIcon = computed(() => {
  if (!sparksStore.hasActiveSubscription) return PhXCircle;
  if (sparksStore.subscriptionWillCancel) return PhWarning;
  return PhCheckCircle;
});

const subscriptionIconBg = computed(() => {
  if (!sparksStore.hasActiveSubscription) return 'bg-gray-400';
  if (sparksStore.subscriptionWillCancel) return 'yellow';
  return 'green';
});

const productUsage = computed(() => {
  const usageStats = sparksStore.getUsageStats(Number(chartPeriod.value)).value;
  const total = usageStats.totalSpent;

  return Object.entries(usageStats.productUsage)
    .map(([product, sparks]) => ({
      name: formatProductName(product),
      description: getProductDescription(product),
      icon: getProductIcon(product),
      sparksUsed: sparks,
      percentage: total > 0 ? Math.round((sparks / total) * 100) : 0,
    }))
    .sort((a, b) => b.sparksUsed - a.sparksUsed);
});

const chartData = computed(() => {
  // Mock data for now - in real implementation, this would come from the dashboard data
  const days = Array.from({ length: Number(chartPeriod.value) }, (_, i) => {
    const date = new Date();
    date.setDate(date.getDate() - (Number(chartPeriod.value) - 1 - i));
    return {
      date: date.toISOString().split("T")[0],
      sparks_spent: Math.floor(Math.random() * 50) + 10,
    };
  });
  return days;
});

// Methods
async function handleRefreshTransactions() {
  try {
    await sparksStore.refreshTransactions();
  } catch (error) {
    console.error("Failed to refresh transactions:", error);
  }
}

function formatDate(dateString?: string) {
  if (!dateString) return "";

  const date = new Date(dateString);
  const now = new Date();
  const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);

  if (diffInHours < 24) {
    if (diffInHours < 1) return "Just now";
    return `${Math.floor(diffInHours)}h ago`;
  }

  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 7) return `${diffInDays}d ago`;

  return date.toLocaleDateString();
}

function formatProductName(product: string) {
  const names: Record<string, string> = {
    scenario_builder: "Scenario Builder",
    vidsabio: "VidSabio",
    analytics: "Learner Analytics",
    subscription: "Subscription",
    system: "System",
  };
  return names[product] || product;
}

function getProductDescription(product: string) {
  const descriptions: Record<string, string> = {
    scenario_builder: "Interactive learning scenarios",
    vidsabio: "Video learning experiences",
    analytics: "Learner insights and analytics",
    subscription: "Monthly credits and benefits",
    system: "System operations",
  };
  return descriptions[product] || "";
}

function getProductIcon(product: string): Component {
  const icons: Record<string, Component> = {
    scenario_builder: PhMaskHappy,
    vidsabio: PhFilmSlate,
    analytics: PhChartBar,
    subscription: PhArrowsClockwise,
    system: PhGear,
  };
  return icons[product] || PhSquaresFour;
}

// Lifecycle
onMounted(async () => {
  // Check if data is already loaded (from App.vue initialization)
  if (sparksStore.dashboardData) {
    console.log('Dashboard data already loaded');
    await nextTick();
    welcomeTour.tryAutoStart();
    return;
  }

  // Wait for SDK to be initialized before refreshing dashboard
  if (sparksStore.isInitialized) {
    console.log('SDK initialized, refreshing dashboard...');
    try {
      await sparksStore.refreshDashboard();
    } catch (error) {
      console.error('Failed to refresh dashboard:', error);
    }
    await nextTick();
    welcomeTour.tryAutoStart();
  } else {
    console.log('SDK not initialized, waiting...');
    // If SDK is not initialized yet, wait for it
    const unwatch = watch(
      () => sparksStore.isInitialized,
      async (isInitialized) => {
        if (isInitialized && !sparksStore.dashboardData) {
          console.log('SDK now initialized, refreshing dashboard...');
          try {
            await sparksStore.refreshDashboard();
          } catch (error) {
            console.error('Failed to refresh dashboard:', error);
          }
          await nextTick();
          welcomeTour.tryAutoStart();
          unwatch(); // Stop watching once initialized
        }
      },
      { immediate: true } // Check immediately
    );
  }
});
</script>
